<template lang="pug">
#outing-list
  // title
  .d-flex.flex-row.justify-content-center.mb-1
    .page-title 外出外泊届け一覧

  // filter
  .mb-2.d-flex.flex-row.align-items-center.flex-wrap(v-if="!isBusy")
    span.table-option-text 利用者グループ：
    .filter
      b-form-select(
        id="filter-input"
        v-model="filter"
        :options="filterOptions"
        size="md"
      )

  // outing list
  OutingTable(
    v-model="items"
    :fields="fields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isBusy="isBusy"
    :filter="filter"
    :filterFields="filterFields"
    @onDetail="onDetail"
  )
  // pagenation
  .d-flex.flex-row.justify-content-center(v-if="!isBusy")
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isBusy"
      @change="selectPage"
      hide-goto-end-buttons
    )
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { Route } from "vue-router";
import { userStore, itemListFilterStore } from "@/store/store";
import { errorHandle } from "@/errorHandler";
import Setting from "../../../../public/setting.json";
import { MobileUrl } from "@/commons/constants/MobileUrl";

import OutingsService, { OutingGetModel } from "@/commons/services/Outings";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";

import OutingTable, { OutingTableField, OutingTableItem } from "@/mobile/components/tables/OutingTable.vue";

/** ナビゲーションガードのフックを使えるようにする */
Component.registerHooks(["beforeRouteLeave"]);

/**
 * ### 外出外泊届け一覧コンポーネント
 */
@Component({
  components: {
    OutingTable
  }
})
export default class ReservationList extends Vue {
  /** 外出外泊サービス */
  @Inject("OutingsService") outingsService!: OutingsService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** 読込み中か否か */
  isBusy: boolean = true;

  /** テーブル列名 */
  fields: Array<OutingTableField> = [];
  /** テーブル行の情報 */
  items: Array<OutingTableItem> = [];
  /** フィルタリング条件 */
  filter: any = "";
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** フィルタリング対象カラム名 */
  filterFields: string[] = ["groupId"];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** メッセージ履歴1ページあたりの表示数 */
  perPage: number = Number(Setting.reservation.perPage);
  /** ページングの全体行数 */
  rows: number = 0;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.reservation.pageRange;

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<OutingTableField> = this.createFields();
    this.fields = fields;

    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(groups);
    this.filterOptions = filterOptions;
    // フィルタの復元
    this.restoreFilterSetting();

    // 動的に変更する画面情報を作成する
    this.items = [];
    this.currentRows = this.items.length;
    this.currentPage = 1;
    await this.createView();

    // ページ番号の復元は画面初回表示時でのみ行う
    // pageRowが設定されるまえにcurrentPageを復元してもライブラリがページ遷移をしてくれないので情報取得後にページ番号を復元する
    this.currentPage = this.$route.params.page ? Number(this.$route.params.page) : 1;

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createFields(): Array<OutingTableField> {
    const fields: Array<OutingTableField> = [];
    fields.push({ key: "outing", label: "", sortable: false, sortDirection: "desc", class: "" });
    return fields;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### 利用者グループフィルター用選択項目
   *
   * - 検索用に利用者グループマスターのMAPを作る
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  public createFilterOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    return filterOptions;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name + "-mobile"] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name + "-mobile"];
    }
    return;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // テーブルに渡す各プロパティを作成する
    const outings: Array<OutingGetModel> = await this.getOutings();
    const items: Array<OutingTableItem> = this.createItems(outings);
    this.items = this.items.concat(items);
    this.currentRows = this.items.length;

    this.isBusy = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const start: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymmdd000000000");
      const count: number = await this.outingsService.getCount(userStore, { start, end: 90000101000000000 });
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }
    return;
  }

  /**
   * ### 外出外泊届けを取得する
   */
  public async getOutings(): Promise<Array<OutingGetModel>> {
    // ページ番号の復元がありえるので、ページ番号を復元する
    // 復元後かつページ遷移した場合、復元したcurrentPageよりもthis.currentPageが大きくなるのでその場合はthis.currentPageを使用する
    let currentPage: number = this.$route.params.page ? Number(this.$route.params.page) : 1;
    currentPage = this.currentPage < currentPage ? currentPage : this.currentPage;

    // 過去の外出外泊届けは表示対象外にするため、開始日時を指定する
    const start: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymmdd000000000");
    const startIndex: number = this.currentRows;
    // tableのライブラリでソートやフィルタリング、ページングを管理するので表示対象のよりも多めに取得しておく
    const endIndex: number = this.perPage * (currentPage + this.pageRange);
    const params: any = {
      // 開始日時は今日の0時、終了日時は17桁の90000101000000000
      start: start,
      startIndex: startIndex,
      end: 90000101000000000,
      endIndex: endIndex,
      sortKey: "datetime",
      order: "asc"
    };

    const response: Array<OutingGetModel> = await this.outingsService.gets(userStore, params);

    return response;
  }

  /**
   * ### テーブルの行を作成する
   *
   * - 削除済みは非表示にしたいので、フィルタリングする
   */
  public createItems(outings: Array<OutingGetModel>): Array<OutingTableItem> {
    const items: Array<OutingTableItem> = [];
    for (const outing of outings) {
      if (outing.deleteFlag) {
        continue;
      }
      items.push({ ...outing });
    }
    return items;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    // 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }

    return;
  }

  /**
   * ### 外出外泊届け詳細画面へ遷移する
   */
  public onDetail(item: OutingTableItem): void {
    this.$router.push({ name: MobileUrl.OutingDetail, params: { id: String(item.id), page: String(this.currentPage) } });
    return;
  }

  beforeRouteLeave(to: Route, from: Route, next: Function) {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name + "-mobile", filter: this.filter });
    }
    next();
  }
}
</script>
